import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertTriangle, Loader2, Lock } from "lucide-react";
import Seo from "../components/Seo";
import { useTrip } from "../hooks/useTrip";
import { postQuote, createOrder, getProfile } from "../lib/api";
import { useAuth } from "../hooks/useAuth";
import { money, prettyDate, time12 } from "../lib/format";
import { LOCATIONS } from "../lib/content";
import { toVendorLines } from "../lib/lines";
import { recordPendingOrder, listPendingOrders } from "../lib/pendingOrders";
import { rememberOrderRef } from "../lib/orderRef";
import { classifyLine, hasDeparted, isBlocking, vendorLineIndex } from "../lib/cartHealth";
import PendingPaymentBanner from "../components/PendingPaymentBanner";
import { PasswordField, PASSWORD_RULE, passwordError } from "../components/PasswordField";

const WB = LOCATIONS["west-branch"];

/** One order = one platform LocationId, so the order's location comes from the
 *  cart lines themselves. Falls back to West Branch only for an empty cart. */
const cartLocationId = (lines) => lines.find((l) => l.locationId)?.locationId ?? WB.locationId;

/** A Booking carries exactly ONE platform LocationId, so a cart spanning two
 *  marinas cannot become one order. Derived from the lines' own ids -- no marina
 *  name is used as logic. */
const hasMixedLocations = (lines) =>
  new Set(lines.map((l) => l.locationId).filter(Boolean)).size > 1;

const MIXED_LOCATIONS_MESSAGE =
  "Your trip mixes boats from West Branch and Lake Milton. Each marina is booked separately — " +
  "please check out one at a time.";

const FIELDS = [
  { name: "firstName", label: "First Name", required: true, autoComplete: "given-name" },
  { name: "lastName", label: "Last Name", required: true, autoComplete: "family-name" },
  { name: "email", label: "Email Address", required: true, type: "email", autoComplete: "email" },
  { name: "phone", label: "Mobile Number", required: true, type: "tel", autoComplete: "tel" },
  { name: "address1", label: "Address 1", required: true, autoComplete: "address-line1" },
  { name: "address2", label: "Address 2 (Optional)", required: false, autoComplete: "address-line2" },
  { name: "city", label: "City", required: true, autoComplete: "address-level2" },
  { name: "state", label: "State", required: true, autoComplete: "address-level1" },
  { name: "postalCode", label: "Zip Code", required: true, autoComplete: "postal-code" },
];

const emptyGuest = FIELDS.reduce((a, f) => ({ ...a, [f.name]: "" }), { country: "US" });

export default function Checkout() {
  const navigate = useNavigate();
  const { lines, clear } = useTrip();
  const { signedIn, register, login, email: signedInEmail } = useAuth();
  const [guest, setGuest] = useState(emptyGuest);
  // Optional, opt-IN account creation. Guest checkout stays the default and is
  // never gated behind this.
  const [account, setAccount] = useState({ optIn: false, password: "", confirm: "" });
  const [signIn, setSignIn] = useState({ open: false, password: "", busy: false, error: null });
  const [errors, setErrors] = useState({});
  const [quote, setQuote] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);
  const idemKey = useRef(`wbm-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

  useEffect(() => {
    if (lines.length === 0) return;
    // A past departure is never sent upstream: the vendor prices it at 0.00
    // rather than refusing it, which is how a $0.00 line reached a live cart.
    const live = lines.filter((l) => !hasDeparted(l));
    if (live.length === 0) {
      setQuote(null);
      return;
    }
    postQuote(cartLocationId(live), toVendorLines(live))
      .then(setQuote)
      .catch(() => setQuote(null));
  }, [JSON.stringify(lines)]);

  // Same classification as /cart, so the two pages can never disagree about
  // whether a line is bookable.
  const liveIndexes = useMemo(
    () => lines.map((l, i) => (hasDeparted(l) ? null : i)).filter((i) => i !== null),
    [lines],
  );
  const health = lines.map((line, i) => {
    const sent = liveIndexes.indexOf(i);
    const liveLines = liveIndexes.map((j) => lines[j]);
    const ql = sent >= 0 && quote?.lines ? quote.lines[vendorLineIndex(liveLines, sent)] : null;
    return classifyLine(line, ql, { quoteError: null, pending: listPendingOrders() });
  });
  const blockedLines = lines.filter((l, i) => isBlocking(health[i].state));
  const mixedLocations = hasMixedLocations(lines);

  // Signed in? Prefill from the profile, but leave every field editable for this
  // booking. Nothing here touches the cart.
  useEffect(() => {
    if (!signedIn) return;
    getProfile()
      .then((p) =>
        setGuest((g) => ({
          ...g,
          firstName: g.firstName || p.firstName || "",
          lastName: g.lastName || p.lastName || "",
          email: g.email || p.email || "",
          phone: g.phone || p.phone || "",
          // The vendor's profile carries an address now, so the whole form fills.
          address1: g.address1 || p.address1 || "",
          address2: g.address2 || p.address2 || "",
          city: g.city || p.city || "",
          state: g.state || p.state || "",
          postalCode: g.postalCode || p.postalCode || "",
          country: g.country || p.country || "US",
        })),
      )
      .catch(() => {});
  }, [signedIn]);

  const missingAcks = useMemo(
    () => lines.filter((l) => !(l.acknowledgedPolicyIds || []).length && (l.requiredAckCount || 0) > 0),
    [lines]
  );

  const validate = () => {
    const e = {};
    FIELDS.filter((f) => f.required).forEach((f) => {
      if (!String(guest[f.name] || "").trim()) e[f.name] = `${f.label} is required.`;
    });    if (guest.email && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(guest.email)) {
      e.email = "Please enter a valid email address.";
    }
    if (guest.phone && String(guest.phone).replace(/\D/g, "").length < 10) {
      e.phone = "Please enter a valid 10-digit phone number.";
    }
    if (missingAcks.length) e.acks = "Please re-open each boat and accept its required policies.";
    if (!signedIn && account.optIn) {
      const pwError = passwordError(account.password);
      if (pwError) e.password = pwError;
      else if (account.password !== account.confirm) e.confirm = "Those passwords don't match.";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (submitting) return;
    setServerError(null);
    // A mixed cart is never sent upstream, and no line is dropped or reassigned
    // to make it fit: the customer removes what they don't want in this order.
    if (mixedLocations) {
      setServerError({ userMessage: MIXED_LOCATIONS_MESSAGE });
      return;
    }
    // Re-validated at the last possible moment: a customer can sit on this page
    // long enough for a departure to pass. An unbookable line is never sent.
    if (blockedLines.length > 0) {
      setServerError({
        userMessage:
          `${blockedLines.map((l) => l.displayName).join(", ")} can no longer be booked as ` +
          "selected. Please go back to your trip to re-time or remove it.",
      });
      return;
    }
    if (!validate()) return;
    setSubmitting(true);

    // The booking ALWAYS wins. If registration or the sign-in that follows it
    // fails, we fall through to a guest booking and say so plainly -- and the
    // message never reveals whether the email already had an account.
    let accountState = null;
    if (!signedIn && account.optIn) {
      try {
        await register({ ...guest, state: (guest.state || "").toUpperCase(), password: account.password });
      } catch {
        /* uniform 202 upstream; nothing here is worth telling the customer */
      }
      try {
        await login(guest.email.trim(), account.password);
        accountState = "signed-in";
      } catch {
        accountState = "pending";
      }
    }

    try {
      const res = await createOrder({
        locationId: cartLocationId(lines),
        idempotencyKey: idemKey.current,
        guest,
        quotedTotal: quote?.orderTotal ?? null,
        lines: toVendorLines(lines).map((l, i) => ({
          ...l,
          acknowledgedPolicyIds: i === 0 ? lines[0]?.acknowledgedPolicyIds || [] : [],
        })),
      });
      // The order EXISTS upstream the moment this returns, so the reference is
      // persisted FIRST and the cart cleared SECOND. A failure between the two
      // can then only ever leave a harmless duplicate reference, never a
      // reservation with no record of it. Everything after this point is
      // payment, which may fail, be abandoned, or finish in another tab.
      window.sessionStorage.setItem(`wbm.order.${res.orderConfirmationCode}`, JSON.stringify(res));
      rememberOrderRef(res);
      recordPendingOrder(res, lines);
      clear();
      // Straight into the branded payment hand-off; the confirmation page is the
      // return destination once the vendor confirms the money.
      const acct = accountState ? `&acct=${accountState}` : "";
      navigate(`/booking/payment?code=${encodeURIComponent(res.orderConfirmationCode)}${acct}`);
    } catch (err) {
      // 409/400 keep the cart intact so the guest can adjust.
      setServerError(err.payload || { userMessage: err.message });
      setSubmitting(false);
    }
  };

  if (lines.length === 0) {
    return (
      <>
        <Seo title="Checkout | West Branch Marina" description="Complete your boat reservation." path="/checkout" />
        <div className="mx-auto max-w-3xl px-5 py-32 text-center lg:px-10">
          <h1 className="font-display text-3xl font-extrabold text-navy">Your trip is empty</h1>
          <p className="mx-auto mt-3 max-w-md text-sm text-slate-600">
            There's nothing to check out yet. Pick a date and add a boat first.
          </p>
          <button
            type="button"
            data-testid="checkout-empty-browse-button"
            onClick={() => navigate("/boat-rentals/west-branch")}
            className="mt-7 rounded-full bg-ember px-7 py-3.5 font-bold text-white hover:bg-ember-hover"
          >
            Browse the fleet
          </button>
        </div>
      </>
    );
  }

  return (
    <>
      <Seo title="Checkout | West Branch Marina" description="Enter your details to reserve your boats at West Branch State Park." path="/checkout" />

      <div className="bg-navy pt-24 pb-10 lg:pt-32">
        <div className="mx-auto max-w-7xl px-5 lg:px-10">
          <p className="eyebrow text-lake">Checkout</p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
            Reserve your boats
          </h1>
        </div>
      </div>

      {/* noValidate: this form owns its own inline, announced errors -- native
          constraint tooltips would pre-empt validate() and leave nothing for a
          screen reader to read. */}
      <form onSubmit={submit} noValidate className="mx-auto grid max-w-7xl gap-10 px-5 py-12 lg:grid-cols-[1.4fr_1fr] lg:gap-14 lg:px-10">
        <div>
          <PendingPaymentBanner className="mb-8" />
          <h2 className="font-display text-xl font-bold text-navy">Your details</h2>
          <div data-testid="guest-form" className="mt-5 grid gap-5 sm:grid-cols-2">
            {FIELDS.map((f) => (
              <div key={f.name} className={f.name.startsWith("address") ? "sm:col-span-2" : ""}>
                <label htmlFor={`g-${f.name}`} className="block text-xs font-bold uppercase tracking-wider text-slate-500">
                  {f.label} {f.required && <span className="text-ember">*</span>}
                </label>
                <input
                  id={`g-${f.name}`}
                  data-testid={`guest-${f.name}`}
                  type={f.type || "text"}
                  autoComplete={f.autoComplete}
                  aria-invalid={Boolean(errors[f.name])}
                  aria-describedby={errors[f.name] ? `err-${f.name}` : undefined}
                  value={guest[f.name]}
                  onChange={(e) => setGuest((g) => ({ ...g, [f.name]: e.target.value }))}
                  className={`mt-2 h-12 w-full rounded-xl border px-3 text-sm text-navy ${
                    errors[f.name] ? "border-red-500 bg-red-50" : "border-sand-border bg-white"
                  }`}
                />
                {errors[f.name] && (
                  <p id={`err-${f.name}`} data-testid={`guest-error-${f.name}`} role="alert" className="mt-1 text-xs font-semibold text-red-600">
                    {errors[f.name]}
                  </p>
                )}
              </div>
            ))}
            <div className="sm:col-span-2">
              <label htmlFor="g-country" className="block text-xs font-bold uppercase tracking-wider text-slate-500">
                Country
              </label>
              <select
                id="g-country"
                data-testid="guest-country"
                value={guest.country}
                onChange={(e) => setGuest((g) => ({ ...g, country: e.target.value }))}
                className="mt-2 h-12 w-full rounded-xl border border-sand-border bg-white px-3 text-sm text-navy"
              >
                <option value="US">United States</option>
                <option value="CA">Canada</option>
              </select>
            </div>
          </div>

          {signedIn ? (
            <p data-testid="checkout-signed-in-note" className="mt-6 text-sm text-slate-600">
              Booking as <strong className="font-semibold text-navy">{signedInEmail}</strong>. This trip will appear in your account.
            </p>
          ) : (
            <div data-testid="checkout-account-block" className="mt-8 rounded-2xl border border-sand-border bg-white p-5">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  data-testid="checkout-account-optin"
                  checked={account.optIn}
                  onChange={(e) => setAccount((a) => ({ ...a, optIn: e.target.checked }))}
                  className="mt-1 h-4 w-4 rounded border-sand-border accent-ember"
                />
                <span className="text-sm text-slate-700">
                  <strong className="font-display font-bold text-navy">Create an account</strong> so you can see this trip,
                  pay your balance and rebook faster. Optional — guest checkout works just fine.
                </span>
              </label>

              {account.optIn && (
                <div data-testid="checkout-account-fields" className="mt-5 grid gap-5 sm:grid-cols-2">
                  <PasswordField
                    id="acct-password"
                    testId="checkout-account-password"
                    label="Password"
                    hint={PASSWORD_RULE}
                    value={account.password}
                    onChange={(v) => setAccount((a) => ({ ...a, password: v }))}
                    error={errors.password}
                  />
                  <PasswordField
                    id="acct-confirm"
                    testId="checkout-account-confirm"
                    label="Confirm password"
                    value={account.confirm}
                    onChange={(v) => setAccount((a) => ({ ...a, confirm: v }))}
                    error={errors.confirm}
                  />
                  <p className="sm:col-span-2 text-xs text-slate-500">
                    If we can't finish setting up the account, your reservation still goes through as a guest booking.
                  </p>
                </div>
              )}

              <div className="mt-5 border-t border-sand-border pt-4">
                {!signIn.open ? (
                  <button
                    type="button"
                    data-testid="checkout-signin-toggle"
                    onClick={() => setSignIn((s) => ({ ...s, open: true, error: null }))}
                    className="text-sm font-bold text-lake underline decoration-2 underline-offset-4 hover:text-navy"
                  >
                    Already have an account? Sign in
                  </button>
                ) : (
                  <div data-testid="checkout-signin-block" className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end">
                  <div>
                      <PasswordField
                        id="signin-password"
                        testId="checkout-signin-password"
                        label={`Password for ${guest.email || "your email above"}`}
                        autoComplete="current-password"
                        value={signIn.password}
                        onChange={(v) => setSignIn((s) => ({ ...s, password: v }))}
                      />
                    </div>
                    <button
                      type="button"
                      data-testid="checkout-signin-submit"
                      disabled={signIn.busy}
                      onClick={async () => {
                        setSignIn((s) => ({ ...s, busy: true, error: null }));
                        try {
                          await login(guest.email.trim(), signIn.password);
                          setSignIn({ open: false, password: "", busy: false, error: null });
                        } catch {
                          setSignIn((s) => ({
                            ...s,
                            busy: false,
                            error: "We couldn't sign you in with those details. You can still check out as a guest.",
                          }));
                        }
                      }}
                      className="h-12 rounded-full bg-navy px-6 text-sm font-bold text-white hover:bg-navy/90 disabled:opacity-60"
                    >
                      {signIn.busy ? "Signing in…" : "Sign in"}
                    </button>
                    {signIn.error && (
                      <p data-testid="checkout-signin-error" role="alert" className="sm:col-span-2 text-xs font-semibold text-red-600">
                        {signIn.error}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <div data-testid="ack-recap" className="mt-10 rounded-2xl border border-sand-border bg-sand p-5">
            <h2 className="font-display text-lg font-bold text-navy">Policies you accepted</h2>
            <ul className="mt-3 space-y-1 text-sm text-slate-700">
              {lines.map((l) => (
                <li key={l.id} data-testid={`ack-recap-${l.id}`}>
                  <strong className="font-semibold text-navy">{l.displayName}</strong>:{" "}
                  {(l.acknowledgedPolicyHeadings || []).join(", ") || "policies accepted on the boat page"}
                </li>
              ))}
            </ul>
            {errors.acks && (
              <p data-testid="ack-recap-error" role="alert" className="mt-3 text-xs font-semibold text-red-600">
                {errors.acks}
              </p>
            )}
          </div>
        </div>

        <div className="lg:sticky lg:top-28 lg:self-start">
          <div data-testid="checkout-summary" className="rounded-3xl border border-sand-border bg-white p-6 shadow-xl shadow-navy/5">
            <p className="eyebrow text-lake">Order summary</p>
            <ul className="mt-4 space-y-4 text-sm">
              {lines.map((l, i) => {
                const sent = liveIndexes.indexOf(i);
                const liveLines = liveIndexes.map((j) => lines[j]);
                const ql = sent >= 0 && quote?.lines ? quote.lines[vendorLineIndex(liveLines, sent)] : null;
                const blockedHere = isBlocking(health[i].state);
                return (
                  <li key={l.id} data-testid={`checkout-line-${l.id}`} className="border-b border-sand-border pb-4 last:border-0">
                    <p className="font-display font-bold text-navy">{l.displayName}</p>
                    <p className="text-xs text-slate-600">
                      {prettyDate(l.date)} · {l.tierLabel} · {time12(l.startTime)}–{time12(l.endTime)} · ×{l.quantity}
                    </p>
                    {blockedHere && (
                      <p data-testid={`checkout-line-blocked-${l.id}`} role="alert" className="mt-2 rounded-lg border border-red-300 bg-red-50 p-2 text-xs font-bold text-red-900">
                        {health[i].title} — not included in this total. Go back to your trip to re-time or
                        remove it.
                      </p>
                    )}
                    {ql && !blockedHere && (
                      <div className="mt-2 space-y-1">
                        <div className="flex justify-between"><span className="text-slate-600">Rental</span><span className="font-semibold text-navy">{money(ql.baseAmount)}</span></div>
                        {(ql.lineItems || [])
                          .filter((it) => it.label !== ql.priceName)
                          .map((it, k) => (
                            <div key={it.feeId ?? `${it.label}-${k}`} className="flex justify-between">
                              <span className="text-slate-600">{it.label}</span>
                              <span className="font-semibold text-navy">{money(it.amount)}</span>
                            </div>
                          ))}
                        {ql.taxAmount > 0 && (
                          <div className="flex justify-between">
                            <span className="text-slate-600">Sales tax</span>
                            <span className="font-semibold text-navy">{money(ql.taxAmount)}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>

            {quote && (
              <>
                <div className="mt-4 flex justify-between border-t border-sand-border pt-4">
                  <span className="font-display text-base font-bold text-navy">Total</span>
                  <span data-testid="checkout-total" className="font-display text-xl font-bold text-navy">
                    {money(quote.orderTotal)}
                  </span>
                </div>
                {quote.hasDeposit && (
                  <>
                    <div className="mt-2 flex justify-between text-sm">
                      <span className="font-bold text-navy">Pay today</span>
                      <span data-testid="checkout-pay-today" className="font-bold text-navy">
                        {money(quote.payTodayAmount)}
                      </span>
                    </div>
                    <div className="mt-1 flex justify-between text-sm">
                      <span className="text-slate-600">Balance due at the marina</span>
                      <span data-testid="checkout-balance" className="font-semibold text-navy">
                        {money(quote.balanceDueAmount)}
                      </span>
                    </div>
                  </>
                )}
                <p data-testid="checkout-total-note" className="mt-2 text-xs text-slate-500">
                  {quote.hasDeposit
                    ? `${money(quote.payTodayAmount)} holds your reservation; the remaining ${money(quote.balanceDueAmount)} is paid at the dock when you check in.`
                    : "This is the full price for your trip."}
                </p>
              </>
            )}

            {serverError && (
              <div data-testid="checkout-server-error" role="alert" className="mt-5 flex items-start gap-3 rounded-xl border border-red-300 bg-red-50 p-4">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-700" aria-hidden="true" />
                <div className="text-sm text-red-900">
                  <p>{serverError.userMessage}</p>
                  {serverError.lineNo && (
                    <p className="mt-1 font-semibold">
                      Affected boat: {lines[serverError.lineNo - 1]?.displayName || `line ${serverError.lineNo}`}
                    </p>
                  )}
                  <p className="mt-1 text-xs">Your trip has been kept so you can adjust it.</p>
                </div>
              </div>
            )}

            <button
              type="submit"
              data-testid="place-order-button"
              disabled={submitting || blockedLines.length > 0 || mixedLocations}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-ember px-6 py-4 font-bold text-white transition-colors hover:bg-ember-hover disabled:cursor-not-allowed disabled:bg-slate-300"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                  Reserving…
                </>
              ) : (
                "Reserve & Continue to Payment"
              )}
            </button>

            {mixedLocations && (
              <p data-testid="checkout-mixed-locations" role="alert" className="mt-3 text-xs font-bold text-red-700">
                {MIXED_LOCATIONS_MESSAGE}
              </p>
            )}

            {blockedLines.length > 0 && (
              <p data-testid="checkout-blocked-reason" role="status" className="mt-3 text-xs font-bold text-red-700">
                {blockedLines.map((l) => l.displayName).join(", ")} can no longer be booked as selected.
                Re-time or remove {blockedLines.length === 1 ? "it" : "them"} in your trip to continue.
              </p>
            )}

            <p className="mt-3 flex items-start gap-2 text-xs text-slate-500">
              <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              Your reservation is held once you continue, and is secured when your deposit is paid.
            </p>
          </div>
        </div>
      </form>
    </>
  );
}
